import { UTCDate } from '@date-fns/utc';
import { max } from '@visx/vendor/d3-array';
import { ScaleTime } from '@visx/vendor/d3-scale';

export type ChartDataPoint = {
  date: UTCDate;
  spend: number;
  cumulative_spend: number;
  units: number;
  cumulative_units: number;
};

export type ChartSeries = {
  id: string;
  name: string;
  data: ChartDataPoint[];
};

export type StackedDataPoint = {
  date: UTCDate;
  seriesId: string;
  value: number;
  baseline: number;
  stackTop: number;
  originalPoint: ChartDataPoint;
};

export type StackedSeries = {
  id: string;
  name: string;
  data: StackedDataPoint[];
};

export type StackedTooltipData = {
  series?: StackedSeries;
  point?: StackedDataPoint;
  allSeries: StackedSeries[];
  plannedAtDate: number;
  plannedPoint?: ChartDataPoint;
  date: UTCDate;
  plannedOnly?: boolean;
};

export function getHighlightOpacity(
  isHighlighted: boolean,
  hasHighlight: boolean,
  baseOpacity: number,
  highlightedOpacity: number,
  dimmedOpacity: number
): number {
  if (!hasHighlight) return baseOpacity;
  return isHighlighted ? highlightedOpacity : dimmedOpacity;
}

export function collectUniqueDates(series: Array<{ date: UTCDate }[]>): UTCDate[] {
  const timestamps = Array.from(new Set(series.flatMap(s => s.map(m => m.date.getTime())))).sort();
  return timestamps.map(t => new UTCDate(t));
}

export function getValue(
  d: ChartDataPoint,
  chartType: 'spend' | 'delivery',
  displayMode: 'discrete' | 'cumulative'
): number {
  if (chartType === 'spend') return displayMode === 'cumulative' ? d.cumulative_spend : d.spend;
  return displayMode === 'cumulative' ? d.cumulative_units : d.units;
}

export function createTooltipData(
  allSeries: StackedSeries[],
  plannedData: ChartDataPoint[],
  chartType: 'spend' | 'delivery',
  displayMode: 'discrete' | 'cumulative',
  date: UTCDate,
  series?: StackedSeries,
  point?: StackedDataPoint
): StackedTooltipData {
  const plannedPoint = plannedData.find(m => m.date.getTime() === date.getTime());
  return {
    series,
    point,
    allSeries,
    plannedAtDate: plannedPoint ? getValue(plannedPoint, chartType, displayMode) : 0,
    plannedPoint,
    date,
    plannedOnly: !series && !point
  };
}

export function calculateBarDimensions(
  dates: UTCDate[],
  dateIndex: number,
  dateScale: ScaleTime<number, number, never>,
  marginLeft: number,
  innerWidth: number,
  padding: number = 1
): { x: number; width: number } {
  const x1 = dateScale(dates[dateIndex]) ?? marginLeft;
  let x2: number;
  if (dateIndex < dates.length - 1) {
    x2 = dateScale(dates[dateIndex + 1]) ?? marginLeft + innerWidth;
  } else if (dates.length >= 2) {
    const prevX = dateScale(dates[dateIndex - 1]) ?? marginLeft;
    x2 = x1 + (x1 - prevX);
  } else {
    x2 = marginLeft + innerWidth;
  }

  return {
    x: x1 + padding,
    width: Math.max(1, x2 - x1 - padding * 2)
  };
}

export function calculateValueExtent(
  values: number[],
  bufferRatio: number = 0.2
): [number, number] {
  const maxVal = max(values) || 0;
  const buffer = maxVal * bufferRatio;
  return [0, maxVal + buffer];
}

export function findDataPointAtDate<T extends { date: UTCDate }>(
  data: T[],
  targetDate: UTCDate
): T | undefined {
  return data.find(d => d.date.getTime() === targetDate.getTime());
}

export function formatName(name: string): string {
  return name.length > 45 ? name.slice(0, 20) + '....' + name.slice(name.length - 15) : name;
}

export function formatDate(date: UTCDate): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
